'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import { SuperscaleTutorialCenterModal } from '@/features/tutorial-center/superscale-tutorial-center'
import {
    MODAL_QUERY_PARAM_KEY,
    MODAL_TYPES,
    parseModalParam,
    formatModalParam,
    guardIsTutorialsModal,
} from '@/setup/routing/modal-route'
import { useQueryParams } from '@/utils/url/use-query-params'
import { noOp } from '@/utils/functions/no-op'

export interface ITutorialCenterModalContext {
    isOpen: boolean
    openTutorialCenter: () => void
    closeTutorialCenter: () => void
}

const TutorialCenterModalContext = createContext<ITutorialCenterModalContext>({
    isOpen: false,
    openTutorialCenter: noOp,
    closeTutorialCenter: noOp,
})

interface ITutorialCenterModalProviderProps {
    children: ReactNode
}

export function TutorialCenterModalProvider({ children }: ITutorialCenterModalProviderProps) {
    const { params, getParam, setParam, deleteParam } = useQueryParams<{ [MODAL_QUERY_PARAM_KEY]: string }>()

    const isTutorialsInUrl = useMemo(
        () => guardIsTutorialsModal(parseModalParam(params[MODAL_QUERY_PARAM_KEY])),
        [params]
    )

    const [isOpen, setIsOpen] = useState(() => guardIsTutorialsModal(parseModalParam(getParam(MODAL_QUERY_PARAM_KEY))))

    useEffect(() => {
        setIsOpen(isTutorialsInUrl)
    }, [isTutorialsInUrl])

    const openTutorialCenter = useCallback(() => {
        setIsOpen(true)
        setParam(MODAL_QUERY_PARAM_KEY, formatModalParam(MODAL_TYPES.Tutorials))
    }, [setParam])

    const closeTutorialCenter = useCallback(() => {
        setIsOpen(false)
        deleteParam(MODAL_QUERY_PARAM_KEY)
    }, [deleteParam])

    return (
        <TutorialCenterModalContext.Provider
            value={{
                isOpen,
                openTutorialCenter,
                closeTutorialCenter,
            }}
        >
            {children}
            <SuperscaleTutorialCenterModal
                open={isOpen}
                onOpenChange={(open) => {
                    if (!open) closeTutorialCenter()
                }}
            />
        </TutorialCenterModalContext.Provider>
    )
}

export function useTutorialCenterModal() {
    return useContext(TutorialCenterModalContext)
}
